import { useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import { CirclePlus } from 'lucide-react';
import { TENANT_USER_ROLES, TENANT_USER_ROLES_TRANSLATED } from './enums';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Input from '@/components/form/Input';
import Select from '@/components/form/Select';
import SpinnerButton from '@/components/common/SpinnerButton';
import Error from '@/components/common/Error';

import { useTenantUsers, useInviteTenantUser } from '@/data/tenantUser';

const formSchema = z.object({
  email: z.string().email(),
  role: z.string().min(1, { message: 'Required' }),
  firstname: z.string().min(1, { message: 'Required' }),
  lastname: z.string().min(1, { message: 'Required' })
});

const AddAccountDialog = () => {
  const [open, setIsOpen] = useState(false);
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inviteTenantUser = useInviteTenantUser();
  const tenantUsers = useTenantUsers();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      role: '',
      firstname: '',
      lastname: ''
    }
  });

  const onSubmit = async (values) => {
    console.log(values);
    setSubmitting(true);
    try {
      setApiError('');
      const newUser = await inviteTenantUser(values);
      await tenantUsers.mutate(
        (currentUsers) => [...currentUsers, newUser],
        false
      );
      setSubmitting(false);
      setIsOpen(false);
      // Reset the form after successful submission
      form.reset();
    } catch (err) {
      setApiError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <CirclePlus />
          Invite User
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
          <DialogDescription>
            Invite a new user to the admin panel
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="flex space-x-4">
              <div className="flex-1">
                <Input name="firstname" label="First Name" autoComplete="off" />
              </div>
              <div className="flex-1">
                <Input name="lastname" label="Last Name" autoComplete="off" />
              </div>
            </div>
            <Input name="email" label="Email" type="email" autoComplete="off" />
            <Select
              label="Role"
              name="role"
              placeholder="Select a role for this user"
              options={getUserRoleOptions()}
            />
            {apiError && <Error error={apiError} />}
            <DialogFooter>
              <SpinnerButton loading={submitting}>Invite User</SpinnerButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
function getUserRoleOptions() {
  return Object.entries(TENANT_USER_ROLES)
    .filter(([key]) => key !== 'MEGA' && key !== 'ADMIN_OWNER')
    .map(([key, value]) => ({
      key: key,
      value: value,
      label: TENANT_USER_ROLES_TRANSLATED[value] || value
    }));
}

export default AddAccountDialog;
